/** The List view's confirmations: rerun research, and park, archive or reactivate an idea. */

import { useRerunResearch, useSetDisposition } from '@/api/queries';
import type { Disposition } from '@/api/types';
import { ConfirmDialog } from '@/ui/ConfirmDialog';

import type { PendingIdeaAction } from './IdeaTableRow';

const DISPOSITION_COPY: Record<Disposition, { title: string; verb: string; body: string }> = {
  parked: { title: 'Park this idea?', verb: 'Park idea', body: 'It leaves the active ideas and waits under Parked. Nothing is deleted.' },
  archived: { title: 'Archive this idea?', verb: 'Archive idea', body: 'It moves to Archived. Its research and history are kept.' },
  active: { title: 'Reactivate this idea?', verb: 'Reactivate', body: 'It returns to the active ideas.' },
};

export function IdeaTableDialogs({
  pending,
  setPending,
}: {
  pending: PendingIdeaAction | null;
  setPending: (p: PendingIdeaAction | null) => void;
}) {
  const rerun = useRerunResearch();
  const setDisposition = useSetDisposition();

  return (
    <>
      {pending?.kind === 'rerun' && (
        <ConfirmDialog
          title="Rerun research?"
          confirmLabel="Rerun research"
          pending={rerun.isPending}
          error={rerun.error}
          onClose={() => {
            rerun.reset();
            setPending(null);
          }}
          onConfirm={() => rerun.mutate({ id: pending.idea.id }, { onSuccess: () => setPending(null) })}
        >
          <p>
            <b>{pending.idea.title}</b> is researched again. This uses AI budget. The current scores stay visible until the new run
            finishes, and every run is kept in the idea's history.
          </p>
        </ConfirmDialog>
      )}
      {pending?.kind === 'disposition' && (
        <ConfirmDialog
          title={DISPOSITION_COPY[pending.to].title}
          confirmLabel={DISPOSITION_COPY[pending.to].verb}
          pending={setDisposition.isPending}
          error={setDisposition.error}
          onClose={() => {
            setDisposition.reset();
            setPending(null);
          }}
          onConfirm={() =>
            setDisposition.mutate({ id: pending.idea.id, disposition: pending.to }, { onSuccess: () => setPending(null) })
          }
        >
          <p>
            <b>{pending.idea.title}</b>. {DISPOSITION_COPY[pending.to].body}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}
